import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon } from "@/ds";
import JobPhotoGrid, { MAX_JOB_PHOTOS } from "@/components/uploads/JobPhotoGrid";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";

/**
 * Photos for a job, as a card in the conversation (D1, closed 2026-08-20).
 *
 * A job posted through the thread reached its FixyBees with nothing to look at:
 * the anonymous draft is not allowed to carry photos, and afterwards the
 * conversation had no way to add them. This is that way, on the same
 * propose → confirm → execute contract the job draft and the profile card use.
 * The `action_id` is the whole authorization and Save is the commit point;
 * Dany cannot press it.
 *
 * **The grid is the marketplace's own**, not a chat-shaped copy of it — the
 * same `JobPhotoGrid` the post-a-job wizard and the job page render, so the cap
 * and the upload behaviour cannot drift between the three places a photo can be
 * added.
 *
 * **Nothing here blocks anything.** Ignoring the card or dismissing it leaves
 * the job exactly as it was; the offer is recorded either way, which is what
 * keeps Dany from raising the same job a second time.
 */
export default function DanyJobPhotosWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const [urls, setUrls] = React.useState(() => [...(data?.photo_urls || [])]);
  const [busy, setBusy] = React.useState(false);
  const [phase, setPhase] = React.useState(data?.saved ? "saved" : "draft");
  const [error, setError] = React.useState(null);

  const actionId = data?.action_id;

  const save = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      await executeAction(actionId, { photo_urls: urls }, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setPhase("saved");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch (err) {
      // The job endpoint's own codes travel out through execute untouched, so
      // the same keys the job page uses answer here.
      setError(
        t([`dany.jobPhotos.${err.code}`, `dany.errors.${err.code}`,
           "dany.jobPhotos.failed"], {
          defaultValue: t("dany.jobPhotos.failed"),
          max: MAX_JOB_PHOTOS,
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("dismissed");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "dismissed") return null;

  if (phase === "saved") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-job-photos-saved">
        <p style={heading}>{t("dany.jobPhotos.savedTitle")}</p>
        <p style={body}>
          {urls.length
            ? t("dany.jobPhotos.saved", { count: urls.length })
            : t("dany.jobPhotos.savedEmpty")}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="raised" padding="md" data-testid="dany-job-photos">
      <span style={eyebrow}>
        <Icon name="camera" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.jobPhotos.header")}
      </span>
      {data?.job_title && <p style={heading}>{data.job_title}</p>}
      <p style={body}>{t("dany.jobPhotos.body", { max: MAX_JOB_PHOTOS })}</p>

      <div style={{ marginTop: "var(--space-3)" }}>
        <JobPhotoGrid
          urls={urls}
          onChange={(next) => { setUrls(next); setError(null); }}
          disabled={busy}
          testid="dany-job-photo"
        />
      </div>

      {error && (
        <p style={{ ...body, color: "var(--danger)" }}
           data-testid="dany-job-photos-error">{error}</p>
      )}

      <div style={{
        marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" icon="check"
                disabled={busy || !actionId || urls.length === 0} onClick={save}
                data-testid="dany-job-photos-save">
          {t("dany.jobPhotos.save")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={dismiss} data-testid="dany-job-photos-skip">
          {t("dany.jobPhotos.skip")}
        </Button>
      </div>
    </Card>
  );
}

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  marginTop: "var(--space-2)",
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
